/**
 * useMyRatings — small hook that fetches and caches the current user's
 * ratings once per page session. Cards subscribe to it to know whether
 * to render the like/dislike buttons in their "active" state.
 *
 * The cache is in-memory only — we never need to persist across
 * refreshes because the backend call is cheap and idempotent.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

export function useMyRatings(itemType) {
    const [map, setMap] = useState({}); // { [itemId]: {rating, feedback} }
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get(`/ratings/me${itemType ? `?item_type=${itemType}` : ""}`)
            .then((r) => {
                if (cancelled) return;
                const next = {};
                (r.data || []).forEach((row) => {
                    next[row.item_id] = {
                        rating: row.rating,
                        feedback: row.feedback || "",
                    };
                });
                setMap(next);
                setLoaded(true);
            })
            .catch(() => {
                if (!cancelled) setLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [itemType]);

    const ratingFor = useCallback((id) => map[id] || null, [map]);

    const setRating = useCallback((id, value, feedback) => {
        setMap((prev) => {
            if (value == null) {
                const { [id]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [id]: { rating: value, feedback: feedback || "" } };
        });
    }, []);

    return { ratingFor, setRating, loaded };
}
